import { createContext, useContext, useState, useEffect } from "react";
import { getAllPermissions, getAllUsers } from "@/Data/api/UserData.js";
import { useAuth } from "@/context/AuthContext.jsx";
import type { ChildrenProps, UserContextValue } from "@/types/app";

const UserContext = createContext<UserContextValue | null>(null);

export const UserProvider = ({ children }: ChildrenProps) => {
    const { user } = useAuth();

    const [allUsers, setAllUsers] = useState([]);
    const [allPerms, setAllPerms] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [errorUsers, setErrorUsers] = useState<unknown>(null);

    useEffect(() => {
        if (!user) return;
        const controller = new AbortController();
        const { signal } = controller;

        const fetchUsersAndPermissions = async () => {
            setLoadingUsers(true);
            setErrorUsers(null);
            try {
                const [usersArray, permissions] = await Promise.all([
                    getAllUsers(user, { signal }),
                    getAllPermissions({ signal }),
                ]);

                setAllUsers(usersArray || []);
                setAllPerms(permissions || []);
            } catch (err) {
                if (!controller.signal.aborted) {
                    // console.log(`UserContext fetch error: ${err}`);
                    setErrorUsers(err);
                }
            } finally {
                setLoadingUsers(false);
            }
        };

        fetchUsersAndPermissions();
        return () => controller.abort();
    }, [user]);

    return (
        <UserContext.Provider
            value={{
                allUsers,
                allPerms, // keep raw list
                loadingUsers,
                errorUsers,
            }}
        >
            {children}
        </UserContext.Provider>
    );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useUserData = () => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error("useUserData must be used inside UserProvider");
    }
    return context;
};
